<template>
  <button
    ref="fileLinkRef"
    v-tooltip="{
      content: text,
      theme: 'info-tooltip',
      placement: 'bottom',
      disabled: !showFileNameTooltip
    }"
    type="button"
    class="reference-component__link reference-component__file-link"
    @mousedown.prevent
    @click.prevent.stop="onDownload"
  >
    #F - {{ text }}
  </button>
</template>

<script lang="ts">
import { mixins, Options } from "vue-class-component"
import { Action } from "s-vuex-class"

import ReferenceComponentCommon from "@/components/Reference/helpers/ReferenceComponentCommon"

import { FileMetaData } from "@/models"
import { Actions } from "@/store"
import { attachSlideStreamRecovery } from "@/utils/videoStreamRecovery"

@Options({
  name: "FileReferenceComponent"
})
export default class FileReferenceComponent extends mixins(ReferenceComponentCommon) {
  @Action(Actions.ATTACHMENT_DOWNLOAD) downloadFile: ({
    fileMetadataId,
    fileName
  }: {
    fileMetadataId: string
    fileName: string
  }) => void
  @Action(Actions.ATTACHMENT_VIEW) loadFile: ({
    fileMetadataId
  }: {
    fileMetadataId: string
  }) => Promise<Blob>
  @Action(Actions.ATTACHMENT_VIDEO_URL) videoUrl: ({
    projectId,
    attachmentId
  }: {
    projectId?: string
    attachmentId: string
  }) => Promise<string>

  showFileNameTooltip = false

  get file() {
    return FileMetaData.create(this.info)
  }

  get isImage() {
    return this.file.isImage
  }

  get isVideo() {
    return this.file.isVideo
  }

  async onDownload() {
    if (!this.node.attrs.id || !this.text) return

    if (this.isImage) {
      this.$fancybox.show([
        {
          blob: this.loadFile,
          fileMetadataId: this.node.attrs.id,
          type: "image",
          caption: this.text
        }
      ])
      return
    }

    if (this.isVideo) {
      const src = await this.videoUrl({
        projectId: this.info?.projectId || this.projectId,
        attachmentId: this.node.attrs.id
      })

      if (!src) return

      this.$fancybox.show(
        [
          {
            fileMetadataId: this.node.attrs.id,
            projectId: this.info?.projectId || this.projectId,
            type: "video",
            src,
            caption: this.text
          }
        ],
        {
          Toolbar: {
            autoEnable: false
          },
          on: {
            done: (fancybox, slide) => {
              if (slide.type === "video" && slide.$content) {
                slide.$content.innerHTML = `<video src="${slide.src}" controls> </video>`
                attachSlideStreamRecovery(slide)
              }
            },
            "Carousel.change": () => {
              const videos = document.querySelectorAll("video")
              videos.forEach(video => {
                video.pause()
              })
            }
          }
        }
      )
      return
    }

    this.downloadFile({
      fileMetadataId: this.node.attrs.id,
      fileName: this.text
    })
  }

  mounted() {
    window.addEventListener("resize", this.updateFileNameTooltip)
    this.updateFileNameTooltip()
  }

  updated() {
    this.updateFileNameTooltip()
  }

  updateFileNameTooltip() {
    this.$nextTick(() => {
      const element = this.$refs.fileLinkRef as HTMLElement
      if (!element) return

      this.showFileNameTooltip = element.offsetWidth < element.scrollWidth
    })
  }

  beforeUnmount() {
    window.removeEventListener("resize", this.updateFileNameTooltip)
    this.$fancybox.unbind()
  }
}
</script>
